import React from 'react'
import PropTypes from 'prop-types'

import {
  Text,
  Modal,
  HStack,
  VStack,
  Button,
  Heading,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
} from '@chakra-ui/react'
import Table from '../Table/Table'
import ReactExport from 'react-export-excel-xlsx-fix'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'

const ExcelFile = ReactExport.ExcelFile
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn

const RenderDownload = ({ data, columns }) => {
  if (!columns || !data) return

  return (
    <ExcelFile
      file
      filename="Reporte"
      fileExtension="xlsx"
      element={
        <Button variant="secondary" borderRadius="11px" px={10}>
          <Text size={16} fontWeight={700} color="#fff">
            Descargar reporte de errores.
          </Text>
        </Button>
      }
    >
      <ExcelSheet name="Reporte" data={data}>
        {columns.map((item, key) => (
          <ExcelColumn key={key} label={item.Header} value={item.accessor} />
        ))}
      </ExcelSheet>
    </ExcelFile>
  )
}

const RenderTable = ({ data, columns }) => {
  if (!columns || !data) return

  return <Table data={data} columns={columns} />
}

export function DataLoadError({
  show,
  onClose,
  columns,
  downloadColumns,
  data,
  mode = 'error',
  onContinue,
  continueLabel = 'Continuar de todas formas',
}) {
  const isError = mode === 'error'
  const isWarning = mode === 'warning'
  const isPartial = mode === 'partial'
  // Solo 'error' es bloqueante (sin botón continuar). 'warning' y 'partial'
  // permiten continuar y usan la cabecera naranja de atención.
  const headerColor = isError ? '#1890FF' : '#FAAD14'
  const title = isError
    ? 'Errores en planilla'
    : isPartial
    ? 'Registros con errores'
    : 'Advertencias en planilla'

  const handleClose = () => {
    onClose()
  }

  const handleContinue = () => {
    if (onContinue) onContinue()
  }

  return (
    <Modal
      size="xl"
      isOpen={show}
      onClose={handleClose}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent bgColor="transparent" borderRadius="10px 10px 0px 0px">
        <ModalHeader bgColor={headerColor} borderRadius="10px 10px 0px 0px">
          <Heading textAlign="left" color="#fff" variant="h5">
            {title}
          </Heading>
        </ModalHeader>
        <ModalCloseButton mt="4px" alignSelf="center" color="#fff" />
        <ModalBody
          p="0"
          overflowY="auto"
          minHeight="300px"
          backgroundColor="#fff"
          borderBottom="1px solid #D7D7D7"
        >
          <RenderTable data={data} columns={columns} />
        </ModalBody>
        <ModalFooter p="20px" bgColor="#fff">
          <VStack spacing={7}>
            <HStack spacing="22px" borderRadius={5}>
              <FontAwesomeIcon size="3x" icon={faTriangleExclamation} />
              <Text variant="light" fontSize={16}>
                {isPartial ? (
                  <>
                    Estos registros tienen errores y{' '}
                    <Text fontWeight="bold" display="contents">
                      no se crearán
                    </Text>
                    . Descárgalos para corregirlos —o deja vacías las columnas
                    Centro de costo / Grupo para usar el valor por defecto— y
                    vuelve a subirlos. Los demás registros se crearán al
                    presionar Continuar.
                  </>
                ) : isWarning ? (
                  <>
                    Estos registros no tienen errores:{' '}
                    <Text fontWeight="bold" display="contents">
                      se crearán con los valores por defecto
                    </Text>
                    . Puedes descargar el detalle para revisarlos y luego
                    presionar Continuar.
                  </>
                ) : (
                  <>
                    Es necesario{' '}
                    <Text fontWeight="bold" display="contents">
                      corregir los errores en la planilla y volverla a cargar
                    </Text>{' '}
                    para realizar la acción.
                  </>
                )}
              </Text>
            </HStack>
            <HStack spacing="16px">
              <RenderDownload data={data} columns={downloadColumns || columns} />
              {!isError && onContinue && (
                <Button
                  variant="primary"
                  borderRadius="11px"
                  px={10}
                  onClick={handleContinue}
                >
                  <Text size={16} fontWeight={700} color="#fff">
                    {continueLabel}
                  </Text>
                </Button>
              )}
            </HStack>
          </VStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

DataLoadError.propTypes = {
  show: PropTypes.bool,
  data: PropTypes.array,
  onClose: PropTypes.func,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      Header: PropTypes.string.isRequired,
      accessor: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
  // Columnas opcionales solo para la descarga (Excel). Si no se entrega, la
  // descarga usa las mismas `columns` que la tabla en pantalla.
  downloadColumns: PropTypes.arrayOf(
    PropTypes.shape({
      Header: PropTypes.string.isRequired,
      accessor: PropTypes.string.isRequired,
    }).isRequired,
  ),
  // 'error' (por defecto): modal bloqueante, solo permite descargar y volver.
  // 'warning': modal informativo que además permite continuar de todas formas.
  // 'partial': creación parcial; informa qué filas se separaron y permite
  // continuar creando solo las válidas.
  mode: PropTypes.oneOf(['error', 'warning', 'partial']),
  onContinue: PropTypes.func,
  continueLabel: PropTypes.string,
}

RenderTable.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      Header: PropTypes.string.isRequired,
      accessor: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
}

RenderDownload.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      Header: PropTypes.string.isRequired,
      accessor: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
}
